import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Group,
  Stack,
  Tabs,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useOrgProject } from '@/context/OrgProjectContext/useOrgProject.ts';
import {
  useMutateSuite,
  useSuiteDetail,
} from '@/hooks/api/automationSuites/useAutomationSuites.ts';
import { useSuiteDrafts } from '@/context/SuiteDraftContext/SuiteDraftContext.ts';
import type { SuiteDetail } from '@/types/automationSuites.ts';
import StyledModal from '@/components/StyledModal/StyledModal.tsx';
import { SuiteError } from '@/pages/AutomationSuites/SuiteShared.tsx';
import SuiteMembers from './SuiteMembers.tsx';
import SuiteBlueprint from './SuiteBlueprint.tsx';
import SuiteContext from './SuiteContext.tsx';
import SuiteReadiness from './SuiteReadiness.tsx';
import SuiteReleases from './SuiteReleases.tsx';

export default function AutomationSuite() {
  const { suiteId = '' } = useParams();
  const { orgId, projectId } = useOrgProject();
  return <SuitePage key={JSON.stringify([orgId, projectId, suiteId])} suiteId={suiteId} />;
}

function SuitePage({ suiteId }: { suiteId: string }) {
  const { orgId, projectId, setProjectId } = useOrgProject();
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const requestedTab = searchParams.get('tab');
  const [tab, setTab] = useState<string | null>(
    requestedTab === 'blueprint' || requestedTab === 'context' || requestedTab === 'readiness' || requestedTab === 'releases'
      ? requestedTab
      : 'members',
  );
  const [modal, setModal] = useState<'metadata' | 'archive' | null>(null);
  const query = useSuiteDetail(suiteId, page);
  const store = useSuiteDrafts();
  const clearSuiteDrafts = store.clearSuite;
  useEffect(() => {
    setTab(
      requestedTab === 'blueprint' || requestedTab === 'context' || requestedTab === 'readiness' || requestedTab === 'releases'
        ? requestedTab
        : 'members',
    );
  }, [requestedTab]);
  useEffect(() => {
    const status = (query.error as { response?: { status?: number } } | null)?.response?.status;
    if ([403, 404].includes(status ?? 0)) clearSuiteDrafts(orgId, suiteId);
  }, [query.error, clearSuiteDrafts, orgId, suiteId]);
  const refresh = () => {
    void query.refetch();
  };
  if (query.isError)
    return (
      <Stack>
        <Anchor component={Link} to="/automation-suites">
          Back to Suites
        </Anchor>
        <SuiteError error={query.error} retry={refresh} />
      </Stack>
    );
  if (!query.data || !query.isFetchedAfterMount) return <Text>Loading Suite…</Text>;
  const suite = query.data;
  if (suite.project_id !== projectId && suite.access_scope === 'project')
    return (
      <Stack>
        <Alert title="Suite belongs to another project">
          Switch to the Suite’s project to continue. Pending edits remain with their original
          target.
        </Alert>
        <Button onClick={() => setProjectId(suite.project_id, false)}>Use Suite project</Button>
        <Anchor component={Link} to="/automation-suites">
          Back to project Suites
        </Anchor>
      </Stack>
    );
  const canEdit =
    suite.project_id === projectId &&
    !suite.archived_at &&
    suite.access_scope === 'project' &&
    suite.shared_text_available;
  return (
    <Stack style={{ overflowWrap: 'anywhere' }}>
      <Anchor component={Link} to="/automation-suites">
        Back to Suites
      </Anchor>
      <Group justify="space-between">
        <Title order={1}>{suite.name ?? 'Restricted Suite'}</Title>
        <Group>
          {suite.archived_at && <Badge color="gray">Archived</Badge>}
          {canEdit && (
            <>
              <Button variant="default" onClick={() => setModal('metadata')}>
                Edit Suite
              </Button>
              <Button variant="default" onClick={() => setModal('archive')}>
                Archive Suite
              </Button>
            </>
          )}
        </Group>
      </Group>
      <Text>{suite.description ?? 'Shared description unavailable in this scope.'}</Text>
      {suite.archived_at && (
        <Alert color="gray" title="Archived Suite">
          History is retained. This Suite cannot be changed.
        </Alert>
      )}
      {suite.access_scope === 'granted_automations' && (
        <Alert color="orange" title="Access-limited view">
          Only authorized structure is shown. Membership grants no access.
        </Alert>
      )}
      <Tabs
        value={tab}
        onChange={(next) => {
          setTab(next);
          const params = new URLSearchParams(searchParams);
          if (next === 'members') params.delete('tab');
          else if (next) params.set('tab', next);
          setSearchParams(params, { replace: true });
        }}
        keepMounted={false}
      >
        <Tabs.List>
          <Tabs.Tab value="members">Members</Tabs.Tab>
          <Tabs.Tab value="blueprint">Blueprint</Tabs.Tab>
          <Tabs.Tab value="context">Dependencies / usage</Tabs.Tab>
          <Tabs.Tab value="readiness">Readiness</Tabs.Tab>
          <Tabs.Tab value="releases">Releases</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="members" pt="md">
          <SuiteMembers
            suite={suite}
            page={page}
            onPage={setPage}
            canEdit={canEdit}
            refresh={refresh}
          />
        </Tabs.Panel>
        <Tabs.Panel value="blueprint" pt="md">
          <SuiteBlueprint suite={suite} canEdit={canEdit} refresh={refresh} />
        </Tabs.Panel>
        <Tabs.Panel value="context" pt="md">
          <SuiteContext suite={suite} refreshSuite={refresh} />
        </Tabs.Panel>
        <Tabs.Panel value="readiness" pt="md">
          <SuiteReadiness suite={suite} />
        </Tabs.Panel>
        <Tabs.Panel value="releases" pt="md">
          <SuiteReleases suite={suite} canEdit={canEdit} refresh={refresh} />
        </Tabs.Panel>
      </Tabs>
      {modal && (
        <SuiteMetadata
          key={modal}
          suite={suite}
          kind={modal}
          canEdit={canEdit}
          close={() => setModal(null)}
          refresh={refresh}
        />
      )}
    </Stack>
  );
}

function SuiteMetadata({
  suite,
  kind,
  canEdit,
  close,
  refresh,
}: {
  suite: SuiteDetail;
  kind: 'metadata' | 'archive';
  canEdit: boolean;
  close: () => void;
  refresh: () => void;
}) {
  const { orgId } = useOrgProject();
  const [fence] = useState(suite.head_revision);
  const [name, setName] = useState(suite.name ?? '');
  const [description, setDescription] = useState(suite.description ?? '');
  const mutation = useMutateSuite();
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const stale = fence !== suite.head_revision;
  const submit = async () => {
    if (stale || !canEdit) return;
    try {
      await mutation.mutateAsync({
        orgId,
        projectId: suite.project_id,
        suiteId: suite.id,
        change:
          kind === 'archive'
            ? { kind: 'archive', body: { expected_head_revision: fence, confirm: true } }
            : { kind: 'metadata', body: { expected_head_revision: fence, name, description } },
      });
      if (mounted.current) close();
    } catch {
      /* Show the mutation error without discarding user input. */
    }
  };
  return (
    <StyledModal
      opened
      onClose={() => {
        if (!mutation.isPending) close();
      }}
      title={kind === 'archive' ? 'Archive Suite' : 'Edit Suite'}
    >
      {stale && (
        <Text c="orange">
          Suite changed. Close and reopen this form to review the latest state.
        </Text>
      )}
      {!canEdit && <Text c="orange">This Suite is currently read-only.</Text>}
      {kind === 'archive' ? (
        <Text>
          Archive this Suite? Members and blueprint history are retained. All Suite edits will be
          disabled.
        </Text>
      ) : (
        <>
          <TextInput
            label="Suite name"
            maxLength={200}
            value={name}
            onChange={(event) => setName(event.currentTarget.value)}
            disabled={mutation.isPending}
          />
          <Textarea
            label="Description"
            maxLength={4000}
            value={description}
            onChange={(event) => setDescription(event.currentTarget.value)}
            disabled={mutation.isPending}
          />
        </>
      )}
      {mutation.isError && (
        <>
          <SuiteError error={mutation.error} />
          <Button variant="default" onClick={refresh}>
            Refresh Suite
          </Button>
        </>
      )}
      <Group justify="flex-end">
        <Button variant="default" disabled={mutation.isPending} onClick={close}>
          Cancel
        </Button>
        <Button
          disabled={stale || !canEdit || (kind === 'metadata' && !name.trim())}
          loading={mutation.isPending}
          onClick={() => void submit()}
        >
          {kind === 'archive' ? 'Confirm archive' : 'Save Suite'}
        </Button>
      </Group>
    </StyledModal>
  );
}
